import React, { FC, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DataTable from 'components/shared/DataTable';
import { Column } from 'components/shared/DataTable/DataTable';
import Grid from 'components/UI/Grid';
import Loading from 'components/UI/Loading/Loading';
import { Paper } from '@mui/material';
import { FilterMatchMode } from 'primereact/api';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { UserProfile } from 'models/restTypes';

const StyledGrid = styled(Grid)`
  display: flex;
  flex: unset;
  height: 100%;
  width: 100%;
  align-items: normal;
`;

const DataTableContainer = styled(Paper)`
  overflow: hidden;
  height: 100%;
  background-color: var(--surface-ground);
`;

const UserProfilesDefaultTable: FC<{
  userProfiles: UserProfile[];
  isLoading: boolean;
}> = observer(({ userProfiles, isLoading }) => {
  const { t } = useTranslation('staticData');

  const [filters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
    uid: { value: '', matchMode: FilterMatchMode.CONTAINS },
    profileDescription: { value: '', matchMode: FilterMatchMode.CONTAINS },
    classification: { value: '', matchMode: FilterMatchMode.CONTAINS },
    firstDayOfWeek: { value: '', matchMode: FilterMatchMode.CONTAINS },
    sendingTimePrefMap: { value: '', matchMode: FilterMatchMode.CONTAINS },
    blockRefSystem: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const onRowClick = useCallback(({ data }: DataTableRowClickEvent) => {
    // Handle row click
    // eslint-disable-next-line no-console
    console.log(data);
  }, []);

  const classificationBody = useCallback((rowData: UserProfile) => {
    return rowData.classification || '-';
  }, []);

  const firstDayOfWeekBody = useCallback((rowData: UserProfile) => {
    return rowData.firstDayOfWeek || '-';
  }, []);

  const deactivationTimeBody = useCallback((rowData: UserProfile) => {
    if (!rowData.deactivationTime) return '-';
    return new Date(rowData.deactivationTime).toLocaleString();
  }, []);

  const sendingTimePrefMapBody = useCallback((rowData: UserProfile) => {
    if (!rowData.sendingTimePrefMap) return '-';
    const entries = Object.entries(rowData.sendingTimePrefMap);
    if (entries.length === 0) return 'Empty';

    return (
      <div style={{ whiteSpace: 'pre-line' }}>
        {entries.map(([channel, pref]) => {
          const timePref = pref.sendingTimePref;
          return (
            <div key={channel} style={{ marginBottom: '0.5rem' }}>
              <strong>{channel}:</strong> {timePref.hourFrom}-{timePref.hourTo}
              h, Days: {timePref.weekDays}
            </div>
          );
        })}
      </div>
    );
  }, []);

  const blockRefSystemBody = useCallback((rowData: UserProfile) => {
    if (!rowData.blockRefSystem) return '-';
    const { blockedIds } = rowData.blockRefSystem;
    if (!blockedIds || blockedIds.length === 0) return 'None';
    return blockedIds.join(', ');
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <StyledGrid flex={1}>
      <DataTableContainer elevation={3}>
        <DataTable
          id="UserProfilesDefaultTable"
          showGridlines
          resizableColumns
          value={userProfiles}
          selectionMode="single"
          scrollable
          scrollHeight="flex"
          dataKey="uid"
          emptyMessage={t('empty_message')}
          stripedRows
          filters={filters}
          onRowClick={onRowClick}
          filterDisplay="row"
          globalFilterFields={[
            'uid',
            'profileDescription',
            'classification',
            'firstDayOfWeek',
            'sendingTimePrefMap',
            'blockRefSystem',
          ]}
          paginator
          rows={10}
        >
          <Column field="uid" header={t('uid')} sortable filter />
          <Column
            field="profileDescription"
            header={t('profile_description')}
            sortable
            filter
          />
          <Column
            field="classification"
            header={t('classification')}
            sortable
            filter
            body={classificationBody}
          />
          <Column
            field="firstDayOfWeek"
            header={t('first_day_of_week')}
            sortable
            filter
            body={firstDayOfWeekBody}
          />
          <Column
            field="sendingTimePrefMap"
            header={t('sending_time_pref_map')}
            sortable
            body={sendingTimePrefMapBody}
          />
          <Column
            field="blockRefSystem"
            header={t('block_ref_system')}
            style={{ width: '200px' }}
            sortable
            body={blockRefSystemBody}
          />
          <Column
            field="deactivationTime"
            header={t('deactivation_time')}
            sortable
            body={deactivationTimeBody}
          />
        </DataTable>
      </DataTableContainer>
    </StyledGrid>
  );
});

export default UserProfilesDefaultTable;
